import React, { useMemo, useCallback } from 'react';
import { Box } from '@availity/element';
import { Drawer } from '@mui/material';
import angleDownIcon from '../../../static/assets/icons/angle-down.svg';
import angleRightIcon from '../../../static/assets/icons/angle-right.svg';
import { iconFilters, themeColors } from '../../../static/constants/styles';
import { extractHeadings } from './sidebar';
import PageSidebar from '../PageSidebar';

interface Guide {
  name: string;
  content: string;
}

interface GuideSidebarProps {
  guides: Guide[];
  activeGuide: string;
  activeSection: string;
  expandedGuides: Set<string>;
  theme: 'light' | 'dark';
  sidebarOpen?: boolean;
  onSidebarClose?: () => void;
  onGuideClick: (name: string) => void;
  onSectionClick: (guideId: string, sectionId: string) => void;
}

const GuideSidebar = ({ guides, activeGuide, activeSection, expandedGuides, theme, sidebarOpen = false, onSidebarClose, onGuideClick, onSectionClick }: GuideSidebarProps): JSX.Element => {
  const colors = theme === 'light'
    ? { textSecondary: 'rgba(17, 29, 66, 0.9)', accent: '#C3CAE2' }
    : { textSecondary: 'rgba(239, 243, 255, 0.9)', accent: '#2F3444' };

  const activeGuideData = useMemo(
    () => guides.find((g) => g.name === activeGuide),
    [guides, activeGuide]
  );
  const searchItems = useMemo(
    () => activeGuideData ? [{ id: activeGuideData.name, label: activeGuideData.name }] : [],
    [activeGuideData]
  );
  const handleSearchResultClick = useCallback((id: string) => { onGuideClick(id); }, [onGuideClick]);

  const navItems = (
    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
      {guides.map((guide) => {
        const headings = extractHeadings(guide.content);
        const isExpanded = expandedGuides.has(guide.name);
        return (
          <Box key={guide.name}>
            <Box component="li">
              <Box
                component="button"
                onClick={() => onGuideClick(guide.name)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, width: 1,
                  p: '10px 12px', cursor: 'pointer', borderRadius: '6px', border: 'none',
                  bgcolor: activeGuide === guide.name ? themeColors[theme].sidebarButton : 'transparent',
                  color: themeColors[theme].text, textAlign: 'left',
                  fontFamily: 'Lato', fontSize: 16, fontWeight: 700, lineHeight: '24px', letterSpacing: '0.01em',
                  '&:hover': { bgcolor: themeColors[theme].sidebarButton },
                }}
              >
                <Box component="img"
                  src={isExpanded ? angleDownIcon : angleRightIcon}
                  sx={{ width: 16, height: 16, filter: theme === 'light' ? iconFilters.dark : iconFilters.light }}
                />
                <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {guide.name.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </Box>
              </Box>
            </Box>
            {isExpanded && headings.map((heading, idx) => (
              <Box component="li" key={heading.id} sx={{ pl: 4, mt: idx === 0 ? 0.5 : 0 }}>
                <Box
                  component="button"
                  onClick={() => onSectionClick(guide.name, heading.id)}
                  sx={{
                    display: 'block', width: 1, p: '6px 12px', cursor: 'pointer',
                    borderRadius: '8px', border: 'none',
                    bgcolor: activeSection === `${guide.name}-${heading.id}` ? colors.accent : 'transparent',
                    color: colors.textSecondary, textAlign: 'left',
                    fontFamily: 'Lato', fontSize: 16, fontWeight: 400, lineHeight: '24px', letterSpacing: '0.01em',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: theme === 'light' ? 'rgba(0, 12, 48, 0.1)' : 'rgba(255, 255, 255, 0.1)' },
                  }}
                >
                  {heading.text}
                </Box>
              </Box>
            ))}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <>
      <Drawer
        anchor="left"
        open={sidebarOpen}
        onClose={onSidebarClose}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 280, bgcolor: themeColors[theme].background } }}
      >
        <PageSidebar theme={theme} searchItems={searchItems} onSearchResultClick={handleSearchResultClick}>{navItems}</PageSidebar>
      </Drawer>

      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <PageSidebar theme={theme} searchItems={searchItems} onSearchResultClick={handleSearchResultClick}>{navItems}</PageSidebar>
      </Box>
    </>
  );
};

export default GuideSidebar;
